import { type DependencyList, useCallback, useEffect, useRef, useState } from 'react';
import { errorMessage } from './api';

export interface Resource<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
  /** Re-runs the loader — call it after every mutation. */
  reload: () => void;
}

/**
 * Fetch on navigation, refetch after mutations: the whole client state strategy.
 * The loader is read through a ref so callers can pass an inline closure while the
 * dependency list stays the explicit trigger.
 */
export function useResource<T>(load: () => Promise<T>, deps: DependencyList): Resource<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    let active = true;
    setLoading(true);
    loadRef.current()
      .then((result) => {
        if (!active) return;
        setData(result);
        setError(null);
      })
      .catch((cause: unknown) => {
        if (!active) return;
        setError(errorMessage(cause));
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [...deps, tick]);

  const reload = useCallback(() => setTick((value) => value + 1), []);
  return { data, error, loading, reload };
}

/** Long enough to stop chasing every keystroke, short enough to feel immediate. */
export const INLINE_DEBOUNCE_MS = 400;

/** The same breakpoint the stylesheet switches the layout at. */
const WIDE_SCREEN = '(min-width: 56rem)';

/** True on tablet and laptop, where a panel can afford to stay open. */
export function useWideScreen(): boolean {
  const [wide, setWide] = useState(() => window.matchMedia(WIDE_SCREEN).matches);

  useEffect(() => {
    const query = window.matchMedia(WIDE_SCREEN);
    const update = () => setWide(query.matches);
    update();
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);

  return wide;
}
